import React, { useState } from 'react';
import { CheckCheck, RotateCcw, XCircle, Loader2 } from 'lucide-react';
import { TaskStatus, TASK_STATUS_LABELS, TASK_STATUS_COLORS } from '../../../types/annotation';

interface FinalReviewPanelProps {
  taskId: string;
  currentStatus: TaskStatus;
  latestReview?: { status: string; comments?: string };
  onFinalReview: (
    status: 'final_accepted' | 'final_rejected' | 'revision_needed',
    comments: string,
  ) => Promise<void>;
}

type FinalAction = 'final_accepted' | 'final_rejected' | 'revision_needed';

const ACTION_CONFIG: Record<
  FinalAction,
  { label: string; icon: React.ReactNode; buttonClass: string; confirmText: string }
> = {
  final_accepted: {
    label: 'Final Accept',
    icon: <CheckCheck className="w-4 h-4" />,
    buttonClass: 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-400',
    confirmText: 'Final-accept this task? This locks it as gold-standard data.',
  },
  revision_needed: {
    label: 'Needs Further Revision',
    icon: <RotateCcw className="w-4 h-4" />,
    buttonClass: 'bg-orange-500 text-white hover:bg-orange-600 focus:ring-orange-400',
    confirmText: 'Send this task back to the supervisor for further revision?',
  },
  final_rejected: {
    label: 'Final Reject',
    icon: <XCircle className="w-4 h-4" />,
    buttonClass: 'bg-rose-700 text-white hover:bg-rose-800 focus:ring-rose-400',
    confirmText: 'Final-reject this task? This is a terminal state.',
  },
};

const FinalReviewPanel: React.FC<FinalReviewPanelProps> = ({
  currentStatus,
  latestReview,
  onFinalReview,
}) => {
  const [comments, setComments] = useState('');
  const [pending, setPending] = useState<FinalAction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleConfirm = async (action: FinalAction) => {
    setPending(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await onFinalReview(action, comments);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit final review.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative bg-white border border-indigo-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-indigo-100 bg-indigo-50/50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-indigo-800">Final Review (Sub-Admin)</h3>
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full ${TASK_STATUS_COLORS[currentStatus]}`}
        >
          {TASK_STATUS_LABELS[currentStatus]}
        </span>
      </div>

      <div className="px-4 py-4 space-y-4">
        {latestReview?.comments && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Supervisor's comment
            </p>
            <p className="text-sm text-gray-700">{latestReview.comments}</p>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Final Review Comment
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={isSubmitting}
            rows={4}
            placeholder="Add a comment (recommended for revision or reject)..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none disabled:opacity-50"
          />
        </div>

        {submitError && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {submitError}
          </p>
        )}

        <div className="flex gap-2">
          {(Object.keys(ACTION_CONFIG) as FinalAction[]).map((action) => {
            const config = ACTION_CONFIG[action];
            return (
              <button
                key={action}
                type="button"
                onClick={() =>
                  action === 'revision_needed' ? handleConfirm(action) : setPending(action)
                }
                disabled={isSubmitting}
                className={[
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1',
                  config.buttonClass,
                  isSubmitting ? 'opacity-60 cursor-not-allowed' : '',
                ].join(' ')}
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : config.icon}
                {config.label}
              </button>
            );
          })}
        </div>
      </div>

      {pending && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-xl">
          <div className="mx-4 bg-white border border-gray-200 rounded-xl shadow-lg p-6 max-w-sm w-full">
            <p className="text-sm font-semibold text-gray-800 mb-2">Confirm Action</p>
            <p className="text-sm text-gray-600 mb-5">{ACTION_CONFIG[pending].confirmText}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPending(null)}
                className="flex-1 px-3 py-1.5 text-sm rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirm(pending)}
                className={[
                  'flex-1 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors',
                  ACTION_CONFIG[pending].buttonClass,
                ].join(' ')}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinalReviewPanel;
